import type { ShippingReading } from '../Sales/shipping';

export interface Customer {
  id: number;
  /** The Customer ID people quote. Minted at creation, never edited. */
  customer_code: string;
  name: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  phone?: string;
  address?: string;
  shipping_address?: string;
  software_terms?: string;
  plastics_version?: string;
  waiver_type?: string;
  gps_type?: string;
  notes?: string;
  latitude?: number;
  longitude?: number;
  is_customer: boolean;
  is_lessee: boolean;
  is_renter: boolean;
  is_purchaser: boolean;
  opening_balances?: OpeningBalance[];
  equipment_summary?: Record<string, {
    drone_ls: number;
    drone_pyro: number;
    battery: number;
    charger: number;
    ground_station: number;
    gear_pelican: number;
    drone_carrier: number;
  }>;
}

/** One item group on the customer page: bought, leased and rented, with what has left the building. */
export interface CustomerTotal {
  key: string;
  label: string;
  purchased: number;
  purchased_shipped: number;
  leased: number;
  leased_out: number;
  rented: number;
  rented_out: number;
}

export interface CustomerOrderColumn {
  key: string;
  label: string;
}

export interface CustomerShipmentLine {
  label: string;
  quantity: number;
  drone_serial?: string | null;
}

export interface CustomerOrderShipment {
  id: string;
  shipped_on: string | null;
  direction: 'out' | 'return';
  tracking_number: string | null;
  created_by_name: string | null;
  lines: CustomerShipmentLine[];
  slip_url?: string | null;
}

export interface CustomerOrder {
  id: string;
  so_number: string;
  order_type: OrderType;
  status: string;
  created_at: string;
  reading: ShippingReading;
  /** Per column key; a key is absent when the order has none of that item. */
  items: Record<string, { ordered: number; shipped: number; returned: number }>;
  last_shipped_on: string | null;
  shipments: CustomerOrderShipment[];
}

/** A hand-typed shipment from before orders carried shipments. */
export interface EarlierShipment {
  id: string;
  shipped_on: string | null;
  tracking_number: string | null;
  notes: string | null;
  lines: { label: string; quantity: number; direction?: 'out' | 'return'; drone_serial?: string | null }[];
}

/** GET /customers/:id/orders */
export interface CustomerOrders {
  totals: CustomerTotal[];
  columns: CustomerOrderColumn[];
  orders: CustomerOrder[];
  earlier: EarlierShipment[];
}

export interface SalesOrder {
  id: string;
  so_number: string;
  order_type: string;
  status: string;
  delivery_date?: string;
  effective_date?: string;
  end_date?: string;
  ship_date?: string;
  return_date?: string;
  created_at: string;
  creator_firstname?: string;
  org_name?: string;
  customer_name?: string;
  customer_org_id?: number;
  notes?: string;
  lines?: { id: string; item_type: string; quantity: number; notes?: string }[];
  /** The order list's shipping reading; absent on rows from other endpoints. */
  shipping?: { state: 'not_shipped' | 'partly' | 'shipped'; shipped: number; total: number; returned?: number };
}

export interface CustomerNote {
  id: string;
  organization_id: number;
  user_id: string;
  message_text: string;
  source: string;
  firstname?: string;
  lastname?: string;
  created_at: string;
}

export interface OpeningBalance {
  id: string;
  organization_id: number;
  balance_type: 'leased' | 'rented' | 'purchased';
  ls_drone_count: number;
  pyro_drone_count: number;
  battery_count: number;
  charger_count: number;
  ground_station_count: number;
  gear_pelican_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface LeaseAdjustment {
  id: string;
  order_id?: string;
  organization_id: number;
  ls_drone_count: number;
  pyro_drone_count: number;
  battery_count: number;
  charger_count: number;
  ground_station_count: number;
  gear_pelican_count: number;
  drone_carrier_count?: number;
  effective_date: string;
  end_date?: string;
  status: string;
  notes?: string;
  so_number?: string;
  created_at: string;
}

export interface EffectiveLeaseCounts {
  ls_drones: number;
  pyro_drones: number;
  batteries: number;
  chargers: number;
  ground_stations: number;
  gear_pelicans: number;
  drone_carriers: number;
}

export interface OrderLineItem {
  item_type: string;
  quantity: number;
  notes?: string;
}

export interface CustomerRental {
  id: string;
  so_number: string;
  status: string;
  effective_date: string;
  end_date: string;
  notes?: string;
  lines: { item_type: string; quantity: number }[];
}

export type OrderType = 'sale' | 'lease' | 'rental';

export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  sale: 'Sale',
  lease: 'Lease',
  rental: 'Rental',
};

export const ORDER_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-[rgb(95_95_95/0.2)] text-on-surface-variant ring-[rgb(95_95_95/0.3)]',
  confirmed: 'bg-blue-500/20 text-blue-400 ring-blue-500/30',
  in_production: 'bg-orange-500/20 text-orange-400 ring-orange-500/30',
  ready: 'bg-purple-500/20 text-purple-400 ring-purple-500/30',
  shipping: 'bg-yellow-500/20 text-yellow-400 ring-yellow-500/30',
  shipped: 'bg-green-500/20 text-green-400 ring-green-500/30',
  delivered: 'bg-green-500/20 text-green-400 ring-green-500/30',
  awaiting_return: 'bg-orange-500/20 text-orange-400 ring-orange-500/30',
  returned: 'bg-[rgb(95_95_95/0.2)] text-on-surface-variant ring-[rgb(95_95_95/0.3)]',
  cancelled: 'bg-red-500/20 text-red-400 ring-red-500/30',
};

// ITEM_TYPES used to live here too, with its own third set of labels ("Drones
// (Standard)") and no importers. Deleted 2026-08-03: the vocabulary is
// per-instance now and lives in Sales/itemTypes.ts. Import it from there.

export const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  lessee: { label: 'Lease', className: 'bg-blue-500/20 text-blue-400 ring-blue-500/30' },
  renter: { label: 'Rental', className: 'bg-orange-500/20 text-orange-400 ring-orange-500/30' },
  purchaser: { label: 'Purchase', className: 'bg-green-500/20 text-green-400 ring-green-500/30' },
};

export type FilterType = '' | 'lessee' | 'renter' | 'purchaser';

export function getStatusBadges(c: Customer): string[] {
  const badges: string[] = [];
  if (c.is_lessee) badges.push('lessee');
  if (c.is_renter) badges.push('renter');
  if (c.is_purchaser) badges.push('purchaser');
  return badges;
}
